import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, Send, Loader2, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * /app/email-templates — Admin-only preview panel for every transactional
 * email in the app. Left rail lists template kinds; main pane shows a live
 * iframe preview (loaded via authenticated fetch → srcdoc since iframes
 * can't send Authorization headers on their own). "Send test to me" fires
 * a real Resend delivery for eyeball verification.
 */
export default function EmailTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/email-templates");
        setTemplates(data.templates);
        if (data.templates.length) setSelected(data.templates[0]);
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to load templates"); }
    })();
  }, []);

  useEffect(() => { if (user?.email) setTestTo(user.email); }, [user]);

  // Fetch preview HTML via authenticated call — set as iframe srcdoc so we
  // don't have to expose the JWT in the URL.
  useEffect(() => {
    if (!selected) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/email-templates/${selected.kind}/preview`);
        setPreviewHtml(typeof data === "string" ? data : "");
      } catch (err) { setPreviewHtml("<p style='padding:16px;color:#b00;font-family:sans-serif'>Preview failed to load.</p>"); }
    })();
  }, [selected]);

  const openInNewTab = () => {
    if (!previewHtml) return;
    const blob = new Blob([previewHtml], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const sendTest = async () => {
    if (!selected) return;
    if (!testTo || !testTo.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${selected.kind}/send-test`, { to: testTo });
      const status = data.email?.status;
      if (status === "sent") toast.success(`Sent — check ${data.to}`);
      else if (status === "mocked") toast.warning("Sent in MOCK mode — RESEND_API_KEY not configured");
      else toast.error(`Send failed: ${JSON.stringify(data.email?.provider_response)?.slice(0, 120)}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline text-primary">Admin</p>
        <h1 className="font-dashboard text-3xl font-bold tracking-tight flex items-center gap-2">
          <Mail className="h-7 w-7 text-primary" /> Email templates
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Preview every transactional email the app can send — with sample data pre-filled. Fire a real test to yourself to double-check deliverability.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        <Card className="border-border bg-white p-2 h-fit">
          <div className="overline px-3 py-2 text-muted-foreground">Templates</div>
          <div className="space-y-0.5">
            {templates.map((t) => (
              <button
                key={t.kind}
                onClick={() => setSelected(t)}
                className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${
                  selected?.kind === t.kind ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
                }`}
                data-testid={`template-tab-${t.kind}`}
              >
                <div className="flex items-center gap-2 font-medium">
                  <FileText className="h-3.5 w-3.5 opacity-70" />
                  {t.label}
                </div>
                <div className={`mt-0.5 text-[11px] ${selected?.kind === t.kind ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{t.description}</div>
              </button>
            ))}
          </div>
        </Card>

        <div className="space-y-4">
          {selected && (
            <>
              <Card className="border-border bg-white p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <div className="overline text-muted-foreground">Send test email to</div>
                    <Input
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="test-email-to"
                    />
                  </div>
                  <Button onClick={sendTest} disabled={busy} className="gap-1" data-testid="send-test-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send test
                  </Button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Subject: <b>[TEST] {selected.subject}</b> · Sender: <b>donotreply@reliservewealth.com</b>
                </p>
              </Card>

              <Card className="overflow-hidden border-border bg-white">
                <div className="border-b border-border px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
                  <span>Preview · sample data</span>
                  <button onClick={openInNewTab} className="text-primary hover:underline" data-testid="open-preview-newtab">
                    Open full preview →
                  </button>
                </div>
                <iframe
                  key={selected.kind}
                  title={selected.label}
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full"
                  style={{ height: 780, border: 0, background: "#F6F4F1" }}
                  data-testid="template-preview-iframe"
                />
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
